/**
 * mapDeliveryChoices — adapter for the cart `deliveryChoices` API.
 *
 * `GET /api/cart/deliveryChoices/:optionCode` returns every size of an option in
 * one payload, each with its own delivery/price options. That is a different
 * shape from checkDeliveryDetails (v2), but the same information, so it is
 * reshaped into `RawSlaOption` here and handed to `mapSlaOptions`. Dedupe by
 * priceType, Best/Fastest ordering, tag labels and the whole presentation layer
 * are then shared with the v2 path instead of reimplemented.
 *
 * Pure — no React, no redux.
 */
import { mapSlaOption } from './mapSlaResponse';
import { DeliveryMethod, PriceTypeTags, RawPriceValue, RawSlaOption, SlaViewModel } from './types';

/** One delivery/price option for a single size. */
export interface RawDeliveryChoiceDetail {
  /** Seller option type from deliveryChoices API (`best` / `fastest`). */
  selectedPriceType?: string;
  /** the deliveryMethod equivalent — "RUSH" / "Priority" / "STANDARD" */
  shippingAction?: string;
  cutoffTime?: string;
  deliverySLA?: string;
  deliveryDate?: string;
  eddUpper?: string;
  price?: RawPriceValue;
  wasPriceData?: RawPriceValue;
  discountPercent?: string;
  availableQuantity?: number;
  isSelected?: boolean;
  quantity?: number;
}

/** Variant qualifiers block on an entry — only `selected` is sent (no `options`). */
export interface RawDeliveryChoiceBaseOption {
  variantType?: string;
  selected?: {
    code?: string;
    url?: string;
    variantOptionQualifiers?: { name?: string; qualifier?: string; value?: string }[];
  };
}

/** One size entry of the deliveryChoices response. */
export interface RawDeliveryChoice {
  size?: { name?: string; qualifier?: string; value?: string };
  skuId?: string;
  isDisabled?: boolean;
  deliveryDetails?: RawDeliveryChoiceDetail[];
  baseOptions?: RawDeliveryChoiceBaseOption[];
}

/** Minimal sizeVariants-option shape the size swatches consume. */
export interface SizeVariantOption {
  code: string;
  url: string;
  stock: { stockLevel: number; stockLevelStatus?: 'inStock' | 'lowStock' | 'outOfStock' };
  variantOptionQualifiers: { name?: string; qualifier?: string; value?: string }[];
}

const LOW_STOCK_THRESHOLD = 5;

const stockStatusFor = (availableQuantity?: number) => {
  if (availableQuantity === undefined) return undefined;
  if (availableQuantity === 0) return 'outOfStock' as const;
  return availableQuantity <= LOW_STOCK_THRESHOLD ? ('lowStock' as const) : ('inStock' as const);
};

/**
 * One delivery option → the v2 `RawSlaOption` shape.
 *
 * deliveryChoices keeps price/wasPriceData flat with `discountPercent` as a
 * sibling, where v2 nests them under `price.priceData`; serviceability is a
 * per-size `isDisabled` rather than a per-option flag.
 */
const toRawSlaOption = (
  detail: RawDeliveryChoiceDetail,
  choice: RawDeliveryChoice
): RawSlaOption => {
  const serviceable = !choice.isDisabled;
  const availableQuantity = detail.availableQuantity;
  const optionPriceType = detail.selectedPriceType || 'best';

  return {
    priceType: optionPriceType as RawSlaOption['priceType'],
    // Sole option is treated as selected even if API omits isSelected.
    isSelected:
      Boolean(detail.isSelected) || (choice.deliveryDetails || []).length === 1,
    // No priceGroupId in this payload; selectedPriceType is unique per size after the
    // dedupe in mapSlaOptions, so it is a stable key for the radio group.
    priceGroupId: `${choice.skuId || ''}-${optionPriceType}`,
    price: {
      priceData: { ...(detail.price || {}), discountPercent: detail.discountPercent },
      wasPriceData: detail.wasPriceData
    },
    serviceabilityDetails: {
      serviceability: serviceable,
      productDetails: [
        {
          productCode: choice.skuId || '',
          serviceability: serviceable,
          deliveryMethod: detail.shippingAction as DeliveryMethod,
          deliverySLA: detail.deliverySLA,
          eddUpper: detail.eddUpper,
          cutOffTime: detail.cutoffTime,
          // deliveryChoices carries no delivery fee — treat as free, which is
          // what the UI shows when the v2 fee is 0.
          deliveryFee: '0'
        }
      ]
    },
    stock: {
      stockLevelStatus: stockStatusFor(availableQuantity),
      stockLevel: availableQuantity
    }
  };
};

/**
 * Locate the entry for the selected size: skuId first, then the size label for
 * payloads that don't carry a distinct skuId per size.
 *
 * Falls back to the first entry rather than nothing. Whatever the API returned
 * is delivery information for this option, so it gets shown — matching just
 * decides *which* entry is the most accurate one to show.
 */
export const findDeliveryChoice = (
  choices: RawDeliveryChoice[] = [],
  skuId?: string,
  sizeLabel?: string
): RawDeliveryChoice | undefined => {
  if (!choices.length) return undefined;
  const bySku = skuId ? choices.find(choice => choice && choice.skuId === skuId) : undefined;
  if (bySku) return bySku;
  const byLabel = sizeLabel ? choices.find(choice => choice?.size?.value === sizeLabel) : undefined;
  return byLabel || choices[0];
};

/** `FnlSizeVariant.selected` of an entry — its size qualifiers and sku url. */
export const sizeVariantSelectedOf = (choice?: RawDeliveryChoice) =>
  (choice?.baseOptions || []).find(option => option?.variantType === 'FnlSizeVariant')?.selected;

/**
 * Stock of a size = availableQuantity of its pre-selected seller option, else
 * the best (max) across options — a size is out of stock only when every seller
 * is out.
 */
const stockLevelOf = (choice?: RawDeliveryChoice): number => {
  const details = choice?.deliveryDetails || [];
  if (!details.length) return 0;
  const selected = details.find(detail => detail?.isSelected);
  if (selected) return Number(selected.availableQuantity) || 0;
  return details.reduce((max, detail) => Math.max(max, Number(detail?.availableQuantity) || 0), 0);
};

/**
 * deliveryChoices → the sizeVariants-style option list the size swatches read.
 *
 * With multiseller on, the sizeVariants (stockCheck) call is dropped: this
 * payload already lists every size with its sku, qualifiers and per-seller
 * availableQuantity, so size labels and stock come from here instead.
 */
export const deliveryChoicesToSizeVariants = (
  choices: RawDeliveryChoice[] = []
): SizeVariantOption[] =>
  (Array.isArray(choices) ? choices : []).map(choice => {
    const selected = sizeVariantSelectedOf(choice);
    const stockLevel = choice?.isDisabled ? 0 : stockLevelOf(choice);
    return {
      code: choice?.skuId || selected?.code || '',
      url: selected?.url || '',
      stock: { stockLevel, stockLevelStatus: stockStatusFor(stockLevel) },
      variantOptionQualifiers: selected?.variantOptionQualifiers || []
    };
  });

/**
 * deliveryChoices entry → the same view model the v2 path produces.
 *
 * Options are kept exactly as the API returned them — no reordering and no
 * dedupe by priceType — matching how the PDP renders its SLA list. Only the
 * per-option mapping is shared with the v2 path.
 */
export const mapDeliveryChoice = (
  choice: RawDeliveryChoice | undefined,
  tags?: PriceTypeTags
): SlaViewModel => {
  const options = (choice?.deliveryDetails || []).map(detail =>
    mapSlaOption(toRawSlaOption(detail, choice as RawDeliveryChoice), tags)
  );
  // Sole serviceable card is always the selected option.
  const serviceable = options.filter(option => option.serviceable);
  if (serviceable.length === 1) {
    const onlyId = serviceable[0].priceGroupId;
    options.forEach(option => {
      option.isSelected = option.serviceable && option.priceGroupId === onlyId;
    });
  }
  return {
    options,
    isMulti: options.length > 1,
    isEmpty: options.length === 0
  };
};
